import { useCallback, useEffect, useRef, useState } from 'react';
import { LayoutComponent } from '../LayoutComponent';
import { Flex, Content, ProgressCircle, Switch } from '@adobe/react-spectrum';
import moment from 'moment';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { ToastQueue } from '@adobe/react-spectrum';
import { useTranslation } from 'react-i18next';
import { GetContextPathRequest } from '../../../../services/soap/project/requests/GetContextPathRequest';
import useComonentReload from '../../../../hooks/UseComponentReload';
import { FPAData, FPADataTypes } from '../../../../infra/protected/FPA/FPAData';
import { ListDocumentRequest } from '../../../../services/soap/document_library/requests/ListDocumentRequest';
import { PermanentlyDeleteDocumentRequest } from '../../../../services/soap/document_library/requests/PermanentlyDeleteDocumentRequest';
import DocumentTable from './DocumentTable';
import styles from './DocumentComponent.module.css';

interface DocumentProps {
  selectedItem?: FPAData;
}

function Document({ selectedItem }: DocumentProps) {
  const PAGE_SIZE: number = 100;
  const [reloadComponent] = useComonentReload();
  const { projectService, documentLibraryService, store } = useDependency();
  const [documentsData, setDocumentsData] = useState<any>([]);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [showSubfolders, setShowSubfolders] = useState<boolean>(true);
  const dataRef = useRef<any>({});
  const { t } = useTranslation();

  const getDocumentsData = useCallback(async () => {
    //const { item: documentTypes } = await documentLibraryService.listDocumentType(new ListDocumentTypeRequest(store.Server, store.SessionId));
    //console.log('documentTypes: ', documentTypes);

    let { DOCUMENT: allDocuments } = await documentLibraryService.listDocument(
      new ListDocumentRequest(store.Server, store.SessionId, {
        limit: PAGE_SIZE,
        offset: 0,
        parent: selectedItem?.id,
        orderBy: 'doc_file_name',
        PRIORITIES: ['aa.doc_name'],
        RESTRICTIONS: ['and aa.doc_parent is null'],
        deepSearch: showSubfolders ? 'true' : undefined,
      })
    );

    //console.log('allDocuments', allDocuments);

    const groupedDocuments: any = {};

    // Group documents by categoryName
    allDocuments?.forEach((doc: any) => {
      const category = doc.categoryName || 'No Category';
      if (!groupedDocuments[category]) {
        groupedDocuments[category] = [];
      }

      groupedDocuments[category].push({
        id: doc.id,
        name: doc.name,
        description: String(doc.description || ''),
        validTo: `${moment(doc.validTo).format('DD/MM/YYYY')}_${moment(doc.validTo).format('hh:mm A')}`,
        fileSize: `${formatFileSize(doc.fileSize)}`,
        createdByName: `${doc.createdByName}_${moment(doc.lastChangeDate).format('DD/MM/YYYY | hh:mm A')}`,
        action: doc.id,
        category: doc.category,
        mimeContentType: doc.mimeContentType,
      });
    });

    // Inside  getDocumentsData function, modify the code creating resultArray:
    const resultArray = Object.keys(groupedDocuments)
      .map(category => {
        // Sort each category's documents by validTo
        const sortedDocs = groupedDocuments[category].sort((a: any, b: any) => {
          // Parse the date strings
          const parseDateTime = (dateTimeStr: any) => {
            const [datePart, timePart] = dateTimeStr.split('_');
            const [day, month, year] = datePart.split('/').map(Number);

            let [time, period] = timePart.split(' ');
            let [hours, minutes] = time.split(':').map(Number);

            // Convert to 24-hour format
            if (period.toUpperCase() === 'PM' && hours < 12) {
              hours += 12;
            } else if (period.toUpperCase() === 'AM' && hours === 12) {
              hours = 0;
            }

            return new Date(year, month - 1, day, hours, minutes);
          };

          const dateA = parseDateTime(a.validTo);
          const dateB = parseDateTime(b.validTo);

          return dateA.getTime() - dateB.getTime(); // For ascending order
          // Use this for descending order: return dateB.getTime() - dateA.getTime();
        });

        return {
          count: sortedDocs.length.toString(),
          DOCUMENT: sortedDocs,
          type: category,
          category: sortedDocs[0]?.category || '',
        };
      })
      .sort((a, b) => b.count - a.count);

    //console.log(resultArray);

    setDocumentsData(resultArray);
  }, [documentLibraryService, selectedItem?.id, showSubfolders, store.Server, store.SessionId]);

  useEffect(() => {
    (async () => {
      const { LIST } = await projectService.getContextPath(
        new GetContextPathRequest(
          store.Server,
          store.SessionId,
          Number(selectedItem?.id),
        ),
      );
      let folderID;
      if (selectedItem) {
        const index = selectedItem.type === FPADataTypes.ACTIVITY ? 1 : 0;
        const context = LIST[index]?.CONTEXT;
        folderID =
          selectedItem.type === FPADataTypes.FOLDER
            ? context?.id
            : context?.parentId;
      } else {
        folderID = undefined;
      }
      dataRef.current = { folderID };
      setShowLoader(false);
    })();
  }, [projectService, selectedItem, store.Server, store.SessionId]);

  useEffect(() => {
    void getDocumentsData();
  }, [getDocumentsData, reloadComponent, selectedItem, showSubfolders]);

  const formatFileSize = (bytes: any) => {
    if (bytes >= 1073741824) {
      return (bytes / 1073741824).toFixed(2) + ' GB';
    } else if (bytes >= 1048576) {
      return (bytes / 1048576).toFixed(2) + ' MB';
    } else if (bytes >= 1024) {
      return (bytes / 1024).toFixed(2) + ' KB';
    } else {
      return bytes + ' bytes';
    }
  };

  const deleteRecord = async (recordId: any) => {
    try {
      setShowLoader(true);
      //console.log('record', record);

      let result = await documentLibraryService.permanentlyDeleteDocument(new PermanentlyDeleteDocumentRequest(store.Server, store.SessionId, { id: recordId }));

      // console.log('result', result);
      if (result.result === 'OK') {
        await getDocumentsData();
        ToastQueue.positive(t('record_deleted_successfully', { ns: 'layout_components' }), { timeout: 3000 });
      } else {
        ToastQueue.negative(result.EXCEPTION.reason, { timeout: 3000 });
      }
    } catch (error) {
    } finally {
      setShowLoader(false);
    }
  };

  if (showLoader) {
    return (
      <Flex width="100%" justifyContent={'center'} marginTop={10}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  } else {
    return (
      <Flex direction={'column'} gap={'size-150'} position={'relative'} UNSAFE_className='tblContainer'>
        <Flex direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Content position={'relative'} UNSAFE_className={styles.heading_text}>
            {t('documents', { ns: 'layout_components' })}
          </Content>
          <Switch isSelected={showSubfolders} onChange={setShowSubfolders} UNSAFE_style={{ marginRight: '-5px' }}>
            {showSubfolders ? t('hide_subfolders', { ns: 'layout_components' }) : t('show_subfolders', { ns: 'layout_components' })}
          </Switch>
        </Flex>
        <DocumentTable documentsData={documentsData} deleteRecord={deleteRecord} getDocumentsData={getDocumentsData} selectedItem={selectedItem} />
      </Flex>
    );
  }
}

export const DocumentComponent = LayoutComponent(Document);
